import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { QRScanner } from '../components/QRScanner';
import { QrCode } from 'lucide-react';
import { getUserRole } from '../lib/api';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showQRScanner, setShowQRScanner] = useState(false);
  const { signIn, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const { error: signInError } = await signIn(email, password);
      if (signInError) {
        throw signInError;
      }

      // Race Directors (roleid 3) → Review Queue; Head/Station Judges (4/5) → Set Station
      try {
        const roleResponse = await getUserRole(email);
        if (roleResponse.success && roleResponse.data?.roleid === 3) {
          navigate('/review-penalty');
          return;
        }
        if (
          roleResponse.success &&
          (roleResponse.data?.roleid === 4 || roleResponse.data?.roleid === 5)
        ) {
          navigate('/set-station');
          return;
        }
      } catch {
        // Fall through to dashboard if role lookup fails
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    }
  };

  const handleQRScan = (result: string) => {
    try {
      // Try to parse as JSON first (recommended format: {"email":"user@example.com","password":"pass123"})
      try {
        const data = JSON.parse(result);
        if (data.email && data.password) {
          setEmail(data.email);
          setPassword(data.password);
          setShowQRScanner(false);
          setError(''); // Clear any previous errors
          return;
        }
      } catch (jsonError) {
        // Not JSON, try other formats
      }

      // Try URL format: login?email=user@example.com&password=pass123
      try {
        const url = new URL(result);
        const emailParam = url.searchParams.get('email');
        const passwordParam = url.searchParams.get('password');
        if (emailParam && passwordParam) {
          setEmail(emailParam);
          setPassword(passwordParam);
          setShowQRScanner(false);
          setError(''); // Clear any previous errors
          return;
        }
      } catch (urlError) {
        // Not a URL, try simple format
      }

      // Try simple format: email:password or email|password
      const colonIndex = result.indexOf(':');
      const pipeIndex = result.indexOf('|');
      const separatorIndex = colonIndex !== -1 ? colonIndex : (pipeIndex !== -1 ? pipeIndex : -1);
      
      if (separatorIndex !== -1) {
        const scannedEmail = result.substring(0, separatorIndex).trim();
        const scannedPassword = result.substring(separatorIndex + 1).trim();
        if (scannedEmail && scannedPassword) {
          setEmail(scannedEmail);
          setPassword(scannedPassword);
          setShowQRScanner(false);
          setError(''); // Clear any previous errors
          return;
        }
      }

      // If none of the formats work, show an error
      setError('QR code format not recognized. Expected JSON, URL, or email:password format.');
      setShowQRScanner(false);
    } catch (err) {
      setError('Failed to parse QR code data. Please try again.');
      setShowQRScanner(false);
    }
  };

  return (
    <div className="pen-screen flex items-center justify-center px-5 py-8">
      <div className="pen-card w-full max-w-md border-t-[3px] border-t-sw-yellow">
        <div className="text-center mb-5">
          <img
            src="/HyroxLogo.png"
            alt="HYROX"
            className="h-12 w-auto mx-auto object-contain"
          />
          <p className="mt-3 text-[10px] tracking-[0.14em] uppercase text-sw-muted-2 font-mono font-light">
            HYROX Penalty Logger
          </p>
        </div>

        <h1 className="pen-title">Sign in</h1>
        <p className="pen-lede">Head Judges · Race Directors · Timing Staff</p>

        {error && <div className="pen-error mb-4">{error}</div>}

        <form className="space-y-3" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email-address" className="pen-label">
              Email address
            </label>
            <input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="pen-input"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="password" className="pen-label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="pen-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button type="submit" disabled={loading} className="pen-btn-primary w-full mt-2">
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <Link to="/forgot-password" className="pen-link block mt-4">
          Forgot your password?
        </Link>

        {/* <div className="mt-5">
          {!showQRScanner ? (
            <button
              type="button"
              onClick={() => setShowQRScanner(true)}
              className="w-full min-h-[88px] bg-black border border-sw-border rounded-sm flex flex-col items-center justify-center gap-2 text-[10px] font-extrabold uppercase tracking-[0.1em] text-sw-muted hover:border-sw-yellow hover:text-sw-yellow transition-colors"
              title="Scan QR Code to auto-fill login"
            >
              <QrCode className="w-5 h-5" />
              <span>QR Code Login @ Timing Admin&apos;s Dashboard</span>
            </button>
          ) : (
            <div className="w-full border border-sw-yellow rounded-sm overflow-hidden bg-black">
              <QRScanner onScan={handleQRScan} />
            </div>
          )}
        </div> */}
      </div>
    </div>
  );
}
